import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Save, 
  Trash2, 
  Search, 
  X,
  FileText,
  Image as ImageIcon,
  Tag,
  User,
  Calendar,
  Eye,
  Edit3,
  Check,
  AlertCircle,
  Globe,
  Star,
  Clock,
  BookOpen
} from 'lucide-react';
import { blogFAQService } from '../lib/database';
import type { BlogFAQ } from '../lib/supabase';
import { blogService } from '../lib/database';
import type { BlogPost } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const BlogManager = () => {
  const { user, isAdmin } = useAuth();
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFAQManager, setShowFAQManager] = useState<string | null>(null);
  const [blogFAQs, setBlogFAQs] = useState<BlogFAQ[]>([]);
  const [newFAQ, setNewFAQ] = useState({
    question: '',
    answer: '',
    order_index: 0,
    is_active: true
  });
  const [editingFAQId, setEditingFAQId] = useState<string | null>(null);

  const [newBlog, setNewBlog] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: 'https://images.pexels.com/photos/5212324/pexels-photo-5212324.jpeg?auto=compress&cs=tinysrgb&w=800',
    category: 'Guidance',
    author: 'Dr. Priya Sharma',
    author_image: 'https://images.pexels.com/photos/3769021/pexels-photo-3769021.jpeg?auto=compress&cs=tinysrgb&w=150',
    read_time: '5 min read',
    tags: [] as string[],
    featured: false,
    published: false,
    // SEO Fields
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    focus_keyword: '',
    canonical_url: '',
    og_title: '',
    og_description: '',
    og_image: '',
    twitter_title: '',
    twitter_description: '',
    schema_type: 'BlogPosting'
  });

  const categories = ['Guidance', 'JEE', 'NEET', 'Career', 'Admissions', 'Scholarships', 'College Life', 'Exam Preparation'];
  const authors = [
    'Dr. Priya Sharma',
    'Prof. Rajesh Kumar', 
    'Dr. Anita Verma',
    'Mr. Vikash Singh',
    'Ms. Sneha Patel',
    'Dr. Amit Gupta'
  ];

  useEffect(() => {
    if (isAdmin) {
      fetchBlogs();
    }
  }, [isAdmin, searchQuery]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const data = await blogService.getAll({ search: searchQuery });
      setBlogs(data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  // Generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Auto-generate SEO fields
  const autoGenerateSEO = (title: string, excerpt: string, category: string, author: string) => {
    const slug = generateSlug(title);
    const currentYear = new Date().getFullYear();
    
    return {
      slug,
      meta_title: title,
      meta_description: excerpt,
      meta_keywords: `${category.toLowerCase()} guidance, ${category.toLowerCase()} tips, college admission, education blog, student guidance, ${category.toLowerCase()} preparation, grad vikalp`,
      focus_keyword: `${category.toLowerCase()} guidance`,
      canonical_url: `/blog/${slug}`,
      og_title: title,
      og_description: excerpt,
      twitter_title: title,
      twitter_description: excerpt
    };
  };

  const handleInputChange = (field: string, value: any) => {
    setNewBlog(prev => {
      const updatedState = {
        ...prev,
        [field]: value
      };

      // Auto-generate SEO fields when title, excerpt, category, or author changes
      if (field === 'title' || field === 'excerpt' || field === 'category' || field === 'author') {
        const title = field === 'title' ? value : prev.title;
        const excerpt = field === 'excerpt' ? value : prev.excerpt;
        const category = field === 'category' ? value : prev.category;
        const author = field === 'author' ? value : prev.author;
        
        if (title) {
          const seoFields = autoGenerateSEO(title, excerpt, category, author);
          return {
            ...updatedState,
            ...seoFields,
            og_image: updatedState.image_url
          };
        }
      }

      return updatedState;
    });
  };

  const handleTagsChange = (value: string) => {
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    setNewBlog(prev => ({
      ...prev,
      tags
    }));
  };

  const resetForm = () => {
    setNewBlog({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image_url: 'https://images.pexels.com/photos/5212324/pexels-photo-5212324.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'Guidance',
      author: 'Dr. Priya Sharma',
      author_image: 'https://images.pexels.com/photos/3769021/pexels-photo-3769021.jpeg?auto=compress&cs=tinysrgb&w=150',
      read_time: '5 min read',
      tags: [],
      featured: false,
      published: false,
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      focus_keyword: '',
      canonical_url: '',
      og_title: '',
      og_description: '',
      og_image: '',
      twitter_title: '',
      twitter_description: '',
      schema_type: 'BlogPosting'
    });
  };

  const validateForm = () => {
    if (!newBlog.title.trim()) {
      toast.error('Blog title is required');
      return false;
    }
    if (!newBlog.slug.trim()) {
      toast.error('URL slug is required');
      return false;
    }
    if (!newBlog.excerpt.trim()) {
      toast.error('Blog excerpt is required');
      return false;
    }
    if (!newBlog.content.trim()) {
      toast.error('Blog content is required');
      return false;
    }
    if (!newBlog.author.trim()) {
      toast.error('Author is required');
      return false;
    }
    if (!newBlog.meta_title.trim()) {
      toast.error('Meta title is required for SEO');
      return false;
    }
    if (!newBlog.meta_description.trim()) {
      toast.error('Meta description is required for SEO');
      return false;
    }
    return true;
  };

  const saveBlog = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingId) {
        await blogService.update(editingId, newBlog);
        toast.success('Blog updated successfully!');
        setEditingId(null);
      } else {
        await blogService.create(newBlog);
        toast.success('Blog created successfully!');
        setShowAddForm(false);
      }
      
      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error('Failed to save blog. Please check your permissions.');
    } finally {
      setSaving(false);
    }
  };

  const editBlog = (blog: BlogPost) => {
    setNewBlog({
      title: blog.title,
      slug: blog.slug,
      excerpt: blog.excerpt,
      content: blog.content,
      image_url: blog.image_url,
      category: blog.category,
      author: blog.author,
      author_image: blog.author_image || '',
      read_time: blog.read_time,
      tags: blog.tags || [],
      featured: blog.featured,
      published: blog.published,
      meta_title: blog.title,
      meta_description: blog.excerpt,
      meta_keywords: blog.tags?.join(', ') || '',
      focus_keyword: blog.category.toLowerCase(),
      canonical_url: `/blog/${blog.slug}`,
      og_title: blog.title,
      og_description: blog.excerpt,
      og_image: blog.image_url,
      twitter_title: blog.title,
      twitter_description: blog.excerpt,
      schema_type: 'BlogPosting'
    });
    setEditingId(blog.id);
    setShowAddForm(true);
  };

  const deleteBlog = async (blog: BlogPost) => {
    if (!confirm(`Are you sure you want to delete "${blog.title}"?`)) return;

    try {
      await blogService.delete(blog.id);
      setBlogs(prev => prev.filter(b => b.id !== blog.id));
      toast.success('Blog deleted successfully!');
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Failed to delete blog');
    }
  };

  const fetchBlogFAQs = async (blogId: string) => {
    try {
      const data = await blogFAQService.getByBlogId(blogId);
      setBlogFAQs(data);
    } catch (error) {
      console.error('Error fetching blog FAQs:', error);
      toast.error('Failed to fetch FAQs');
    }
  };

  const saveFAQ = async () => {
    if (!newFAQ.question.trim() || !newFAQ.answer.trim() || !showFAQManager) return;

    try {
      if (editingFAQId) {
        await blogFAQService.update(editingFAQId, newFAQ);
        toast.success('FAQ updated successfully!');
        setEditingFAQId(null);
      } else {
        await blogFAQService.create({
          ...newFAQ,
          blog_id: showFAQManager
        });
        toast.success('FAQ added successfully!');
      }
      
      setNewFAQ({ question: '', answer: '', order_index: 0, is_active: true });
      fetchBlogFAQs(showFAQManager);
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Failed to save FAQ');
    }
  };

  const editFAQ = (faq: BlogFAQ) => {
    setNewFAQ({
      question: faq.question,
      answer: faq.answer,
      order_index: faq.order_index,
      is_active: faq.is_active
    });
    setEditingFAQId(faq.id);
  };

  const deleteFAQ = async (faqId: string) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      await blogFAQService.delete(faqId);
      toast.success('FAQ deleted successfully!');
      if (showFAQManager) {
        fetchBlogFAQs(showFAQManager);
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    resetForm();
  };

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <AlertCircle className="h-16 w-16 text-warning mx-auto mb-3" />
                </div>
                <h1 className="h2 text-dark mb-3">Access Restricted</h1>
                <p className="text-muted mb-4">
                  You need admin privileges to access the Blog Management system.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/" className="btn btn-primary">
                    Back to Home
                  </Link>
                  <Link to="/contact" className="btn btn-outline-primary">
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading blog management...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary rounded-3 p-3">
              <FileText className="text-white" size={32} />
            </div>
            <div>
              <h1 className="h2 fw-bold text-dark mb-1">Blog Manager</h1>
              <p className="text-muted mb-0">Create and manage SEO-optimized blog posts</p>
            </div>
          </div>
          <Link to="/admin" className="btn btn-outline-secondary">
            Back to Admin
          </Link>
        </div>

        {/* Action Bar */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div className="d-flex gap-2">
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true);
                setEditingId(null);
              }}
              className="btn btn-success d-flex align-items-center gap-2"
            >
              <Plus size={16} />
              Add New Blog Post
            </button>
            <span className="badge bg-primary fs-6 px-3 py-2">
              {blogs.length} Posts
            </span>
          </div>

          <div className="position-relative">
            <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
            <input
              type="text"
              placeholder="Search blog posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control ps-5"
              style={{ width: '300px' }}
            />
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card border-0 shadow-lg mb-4"
          >
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">{editingId ? 'Edit Blog Post' : 'Add New Blog Post'}</h5>
            </div>
            <div className="card-body p-4">
              {/* Basic Information */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">📝 Basic Information</h6>
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label fw-semibold">Blog Title *</label>
                    <input
                      type="text"
                      value={newBlog.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="form-control"
                      placeholder="Enter compelling blog title"
                    />
                    <div className="form-text">
                      Recommended: 50-60 characters. Current: {newBlog.title.length}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Category *</label>
                    <select
                      value={newBlog.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="form-select"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">URL Slug *</label>
                    <div className="input-group">
                      <span className="input-group-text">/blog/</span>
                      <input
                        type="text"
                        value={newBlog.slug}
                        onChange={(e) => handleInputChange('slug', e.target.value)}
                        className="form-control"
                        placeholder="url-friendly-slug"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Author *</label>
                    <select
                      value={newBlog.author}
                      onChange={(e) => handleInputChange('author', e.target.value)}
                      className="form-select"
                    >
                      {authors.map(author => (
                        <option key={author} value={author}>{author}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Read Time</label>
                    <input
                      type="text"
                      value={newBlog.read_time}
                      onChange={(e) => handleInputChange('read_time', e.target.value)}
                      className="form-control"
                      placeholder="5 min read"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={newBlog.tags.join(', ')}
                      onChange={(e) => handleTagsChange(e.target.value)}
                      className="form-control"
                      placeholder="college guidance, admission tips, JEE"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Excerpt *</label>
                    <textarea
                      value={newBlog.excerpt}
                      onChange={(e) => handleInputChange('excerpt', e.target.value)}
                      className="form-control"
                      rows={3}
                      placeholder="Brief description of the blog post (150-160 characters recommended)"
                    />
                    <div className="form-text">
                      Recommended: 150-160 characters. Current: {newBlog.excerpt.length}
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Content *</label>
                    <textarea
                      value={newBlog.content}
                      onChange={(e) => handleInputChange('content', e.target.value)}
                      className="form-control"
                      rows={10}
                      placeholder="Write your blog content here... Use HTML tags for formatting."
                    />
                    <div className="form-text">
                      Use HTML tags for formatting: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, etc.
                    </div>
                  </div>
                </div>
              </div>

              {/* SEO Optimization */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">🔍 SEO Optimization</h6>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold">Meta Title *</label>
                    <input
                      type="text"
                      value={newBlog.meta_title}
                      onChange={(e) => handleInputChange('meta_title', e.target.value)}
                      className="form-control"
                      placeholder="SEO-optimized title for search engines"
                    />
                    <div className="form-text">
                      Recommended: 50-60 characters. Current: {newBlog.meta_title?.length || 0}
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Meta Description *</label>
                    <textarea
                      value={newBlog.meta_description}
                      onChange={(e) => handleInputChange('meta_description', e.target.value)}
                      className="form-control"
                      rows={3}
                      placeholder="Description that appears in search results"
                    />
                    <div className="form-text">
                      Recommended: 150-160 characters. Current: {newBlog.meta_description?.length || 0}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Focus Keyword</label>
                    <input
                      type="text"
                      value={newBlog.focus_keyword}
                      onChange={(e) => handleInputChange('focus_keyword', e.target.value)}
                      className="form-control"
                      placeholder="Primary keyword to rank for"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Canonical URL</label>
                    <input
                      type="text"
                      value={newBlog.canonical_url}
                      onChange={(e) => handleInputChange('canonical_url', e.target.value)}
                      className="form-control"
                      placeholder="/blog/your-post-slug"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Meta Keywords</label>
                    <textarea
                      value={newBlog.meta_keywords}
                      onChange={(e) => handleInputChange('meta_keywords', e.target.value)}
                      className="form-control"
                      rows={2}
                      placeholder="SEO keywords separated by commas"
                    />
                  </div>
                </div>
              </div>

              {/* Social Media Optimization */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">📱 Social Media Optimization</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Open Graph Title</label>
                    <input
                      type="text"
                      value={newBlog.og_title}
                      onChange={(e) => handleInputChange('og_title', e.target.value)}
                      className="form-control"
                      placeholder="Title for Facebook/LinkedIn sharing"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Twitter Title</label>
                    <input
                      type="text"
                      value={newBlog.twitter_title}
                      onChange={(e) => handleInputChange('twitter_title', e.target.value)}
                      className="form-control"
                      placeholder="Title for Twitter sharing"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Open Graph Description</label>
                    <textarea
                      value={newBlog.og_description}
                      onChange={(e) => handleInputChange('og_description', e.target.value)}
                      className="form-control"
                      rows={2}
                      placeholder="Description for Facebook/LinkedIn"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Twitter Description</label>
                    <textarea
                      value={newBlog.twitter_description}
                      onChange={(e) => handleInputChange('twitter_description', e.target.value)}
                      className="form-control"
                      rows={2}
                      placeholder="Description for Twitter"
                    />
                  </div>
                </div>
              </div>

              {/* Media & Publishing */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">🖼️ Media & Publishing</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Featured Image URL *</label>
                    <input
                      type="url"
                      value={newBlog.image_url}
                      onChange={(e) => handleInputChange('image_url', e.target.value)}
                      className="form-control"
                      placeholder="https://images.pexels.com/..."
                    />
                    <div className="form-text">
                      Recommended: 1200x630px for optimal social sharing
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Author Image URL</label>
                    <input
                      type="url"
                      value={newBlog.author_image}
                      onChange={(e) => handleInputChange('author_image', e.target.value)}
                      className="form-control"
                      placeholder="https://images.pexels.com/..."
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="featured"
                        checked={newBlog.featured}
                        onChange={(e) => handleInputChange('featured', e.target.checked)}
                        className="form-check-input"
                      />
                      <label className="form-check-label fw-semibold" htmlFor="featured">
                        Featured Post
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="published"
                        checked={newBlog.published}
                        onChange={(e) => handleInputChange('published', e.target.checked)}
                        className="form-check-input"
                      />
                      <label className="form-check-label fw-semibold" htmlFor="published">
                        Publish Immediately
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Formatting Guide */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">📝 Content Formatting Guide</h6>
                <div className="card border">
                  <div className="card-body p-3">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <h6 className="small fw-semibold text-dark mb-2">Basic Formatting:</h6>
                        <div className="small text-muted">
                          <div><code>&lt;h2&gt;Main Heading&lt;/h2&gt;</code></div>
                          <div><code>&lt;h3&gt;Sub Heading&lt;/h3&gt;</code></div>
                          <div><code>&lt;p&gt;Paragraph text&lt;/p&gt;</code></div>
                          <div><code>&lt;strong&gt;Bold text&lt;/strong&gt;</code></div>
                          <div><code>&lt;em&gt;Italic text&lt;/em&gt;</code></div>
                          <div><code>&lt;a href="url"&gt;Link&lt;/a&gt;</code></div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <h6 className="small fw-semibold text-dark mb-2">Advanced Elements:</h6>
                        <div className="small text-muted">
                          <div><code>&lt;ul&gt;&lt;li&gt;List item&lt;/li&gt;&lt;/ul&gt;</code></div>
                          <div><code>&lt;blockquote&gt;&lt;p&gt;Quote&lt;/p&gt;&lt;/blockquote&gt;</code></div>
                          <div><code>&lt;hr /&gt;</code> - Horizontal line</div>
                          <div><code>&lt;code&gt;inline code&lt;/code&gt;</code></div>
                        </div>
                      </div>
                      <div className="col-12">
                        <h6 className="small fw-semibold text-dark mb-2">Images & Tables:</h6>
                        <div className="small text-muted">
                          <div className="mb-2">
                            <strong>Image:</strong><br />
                            <code>&lt;img src="https://images.pexels.com/..." alt="Description" class="img-fluid rounded mb-3" /&gt;</code>
                          </div>
                          <div>
                            <strong>Table:</strong><br />
                            <code>&lt;table class="table table-bordered"&gt;<br />
                            &nbsp;&nbsp;&lt;thead&gt;&lt;tr&gt;&lt;th&gt;Header&lt;/th&gt;&lt;/tr&gt;&lt;/thead&gt;<br />
                            &nbsp;&nbsp;&lt;tbody&gt;&lt;tr&gt;&lt;td&gt;Data&lt;/td&gt;&lt;/tr&gt;&lt;/tbody&gt;<br />
                            &lt;/table&gt;</code>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SEO Preview */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3">👁️ SEO Preview</h6>
                <div className="card border">
                  <div className="card-body p-3">
                    <div className="mb-3">
                      <h6 className="small fw-semibold text-muted mb-2">Google Search Result Preview:</h6>
                      <div className="border p-3 rounded bg-white">
                        <div style={{ fontFamily: 'Arial, sans-serif' }}>
                          <div style={{ color: '#1a0dab', fontSize: '18px', marginBottom: '4px' }}>
                            {newBlog.meta_title || newBlog.title || 'Blog Title'}
                          </div>
                          <div style={{ color: '#006621', fontSize: '14px', marginBottom: '4px' }}>
                            https://gradvikalp.com{newBlog.canonical_url || `/blog/${newBlog.slug || 'post-slug'}`}
                          </div>
                          <div style={{ color: '#545454', fontSize: '14px', lineHeight: '1.4' }}>
                            {newBlog.meta_description || newBlog.excerpt || 'Meta description will appear here...'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h6 className="small fw-semibold text-muted mb-2">Social Media Preview:</h6>
                      <div className="border p-3 rounded bg-white">
                        <div style={{ maxWidth: '400px' }}>
                          <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                            <div style={{ height: '200px', background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {newBlog.image_url ? (
                                <img 
                                  src={newBlog.image_url} 
                                  alt="Social preview" 
                                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} 
                                />
                              ) : (
                                <div style={{ color: '#aaa', textAlign: 'center' }}>
                                  <ImageIcon size={48} />
                                  <div>Image Preview</div>
                                </div>
                              )}
                            </div>
                            <div style={{ padding: '12px' }}>
                              <div style={{ color: '#385898', fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                                {newBlog.og_title || newBlog.title || 'Blog Title'}
                              </div>
                              <div style={{ color: '#606770', fontSize: '14px', marginBottom: '4px' }}>
                                gradvikalp.com
                              </div>
                              <div style={{ color: '#606770', fontSize: '14px', lineHeight: '1.4' }}>
                                {newBlog.og_description || newBlog.excerpt || 'Description will appear here...'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="d-flex gap-2 mt-4">
                <button
                  onClick={saveBlog}
                  disabled={saving}
                  className="btn btn-primary d-flex align-items-center gap-2"
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingId ? 'Update Blog Post' : 'Create Blog Post'}
                    </>
                  )}
                </button>
                <button
                  onClick={cancelEdit}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Blog List */}
        <div className="row g-4">
          {blogs.map((blog) => (
            <div key={blog.id} className="col-12">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card border-0 shadow-sm"
              >
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <img
                        src={blog.image_url}
                        alt={blog.title}
                        className="img-fluid rounded"
                        style={{ height: '120px', width: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex gap-2 mb-2">
                        <span className="badge bg-primary">{blog.category}</span>
                        {blog.featured && <span className="badge bg-warning">Featured</span>}
                        <span className={`badge ${blog.published ? 'bg-success' : 'bg-secondary'}`}>
                          {blog.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      <h5 className="fw-bold text-dark mb-2">{blog.title}</h5>
                      <p className="text-muted small mb-2 line-clamp-2">{blog.excerpt}</p>
                      <div className="vstack gap-1 small text-muted">
                        <div className="d-flex align-items-center gap-2">
                          <User size={14} />
                          <span>{blog.author}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Clock size={14} />
                          <span>{blog.read_time}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Calendar size={14} />
                          <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Globe size={14} />
                          <span className="text-primary">/blog/{blog.slug}</span>
                        </div>
                      </div>
                      {blog.tags && blog.tags.length > 0 && (
                        <div className="mt-2">
                          {blog.tags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="badge bg-light text-dark me-1 small">
                              {tag}
                            </span>
                          ))}
                          {blog.tags.length > 3 && (
                            <span className="badge bg-light text-dark small">
                              +{blog.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-md-3 text-end">
                      <div className="d-flex flex-column gap-2">
                        <Link
                          to={`/blog/${blog.slug}`}
                          target="_blank"
                          className="btn btn-outline-info btn-sm d-flex align-items-center gap-2"
                        >
                          <Eye size={14} />
                          View Post
                        </Link>
                        <button
                          onClick={() => editBlog(blog)}
                          className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                        >
                          <Edit3 size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteBlog(blog)}
                          className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                        <button
                          onClick={() => {
                            setShowFAQManager(blog.id);
                            fetchBlogFAQs(blog.id);
                          }}
                          className="btn btn-outline-info btn-sm d-flex align-items-center gap-2"
                        >
                          <BookOpen size={14} />
                          Manage FAQs
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>

        {blogs.length === 0 && !loading && (
          <div className="text-center py-5">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <BookOpen className="text-primary mx-auto mb-3" size={64} />
                </div>
                <h3 className="h4 text-dark mb-3">No blog posts found</h3>
                <p className="text-muted mb-4">
                  {searchQuery 
                    ? 'No blog posts match your search criteria. Try adjusting your search terms.'
                    : 'Get started by creating your first SEO-optimized blog post.'
                  }
                </p>
                <button
                  onClick={() => {
                    resetForm();
                    setShowAddForm(true);
                    setEditingId(null);
                  }}
                  className="btn btn-primary"
                >
                  Create Your First Blog Post
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FAQ Manager Modal */}
      {showFAQManager && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">Manage Blog FAQs</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowFAQManager(null);
                    setEditingFAQId(null);
                    setNewFAQ({ question: '', answer: '', order_index: 0, is_active: true });
                  }}
                ></button>
              </div>
              <div className="modal-body p-4">
                {/* Add FAQ Form */}
                <div className="card border mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">{editingFAQId ? 'Edit FAQ' : 'Add New FAQ'}</h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-semibold">Question *</label>
                        <input
                          type="text"
                          value={newFAQ.question}
                          onChange={(e) => setNewFAQ(prev => ({ ...prev, question: e.target.value }))}
                          className="form-control"
                          placeholder="Enter FAQ question"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Answer *</label>
                        <textarea
                          value={newFAQ.answer}
                          onChange={(e) => setNewFAQ(prev => ({ ...prev, answer: e.target.value }))}
                          className="form-control"
                          rows={4}
                          placeholder="Enter FAQ answer"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Order Index</label>
                        <input
                          type="number"
                          value={newFAQ.order_index}
                          onChange={(e) => setNewFAQ(prev => ({ ...prev, order_index: parseInt(e.target.value) }))}
                          className="form-control"
                          min="0"
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="form-check mt-4">
                          <input
                            type="checkbox"
                            id="faq-active"
                            checked={newFAQ.is_active}
                            onChange={(e) => setNewFAQ(prev => ({ ...prev, is_active: e.target.checked }))}
                            className="form-check-input"
                          />
                          <label className="form-check-label fw-semibold" htmlFor="faq-active">
                            Active
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="d-flex gap-2 mt-4">
                      <button
                        onClick={saveFAQ}
                        className="btn btn-primary d-flex align-items-center gap-2"
                      >
                        <Save size={16} />
                        {editingFAQId ? 'Update FAQ' : 'Add FAQ'}
                      </button>
                      {editingFAQId && (
                        <button
                          onClick={() => {
                            setEditingFAQId(null);
                            setNewFAQ({ question: '', answer: '', order_index: 0, is_active: true });
                          }}
                          className="btn btn-secondary"
                        >
                          Cancel Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* FAQ List */}
                <div className="card border">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Existing FAQs ({blogFAQs.length})</h6>
                  </div>
                  <div className="card-body">
                    {blogFAQs.length > 0 ? (
                      <div className="vstack gap-3">
                        {blogFAQs.map((faq, index) => (
                          <div key={faq.id} className="card border">
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1">
                                  <h6 className="fw-bold text-dark mb-1">
                                    {index + 1}. {faq.question}
                                  </h6>
                                  <p className="text-muted mb-2">{faq.answer}</p>
                                  <div className="d-flex gap-2">
                                    <span className={`badge ${faq.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                      {faq.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    <span className="badge bg-light text-dark">
                                      Order: {faq.order_index}
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex gap-2">
                                  <button
                                    onClick={() => editFAQ(faq)}
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    <Edit3 size={14} />
                                  </button>
                                  <button
                                    onClick={() => deleteFAQ(faq.id)}
                                    className="btn btn-sm btn-outline-danger"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted">No FAQs added yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager;